import React, { useEffect, useState } from 'react';
import {
    Box, Heading, SimpleGrid, Card, CardBody, Text, Spinner, Center, VStack,
    Button, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Select, Input,
    useToast, Flex
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

interface RoomItem {
    _id: string;
    luogo: string;
    data: string;
    titoloEvento: string;
    totalSeats?: number;
    availableSeats?: number;
}

const RoomSelectionPage: React.FC = () => {
    const [rooms, setRooms] = useState<RoomItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const [selectedConfig, setSelectedConfig] = useState('fai');
    const [eventDate, setEventDate] = useState('');
    const [selectedPrice, setSelectedPrice] = useState(0);
    const [titoloEvento, setTitoloEvento] = useState('');
    const [immagineBase64, setImmagineBase64] = useState('');
    const toast = useToast();

    const fetchRooms = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/rooms`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('fetch rooms failed');
            const data = await res.json();
            setRooms(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRooms();
    }, []);

    const handleCreateRoom = async () => {
        if (!eventDate) {
            toast({
                title: "Errore",
                description: "Seleziona una data per l'evento",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/create-room`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    config: selectedConfig,
                    date: eventDate,
                    price: selectedPrice,
                    titoloEvento: titoloEvento,
                    immagine: immagineBase64
                }),
            });

            if (!res.ok) throw new Error(await res.text());

            onCreateClose();

            // Reset dei campi
            setImmagineBase64('');
            setTitoloEvento('');
            setEventDate('');
            setSelectedPrice(0);
            setSelectedConfig('fai');

            toast({
                title: "Sala creata con successo.",
                description: "La nuova sala è stata creata correttamente.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            // Ricarica la lista delle sale
            fetchRooms();
        } catch (err) {
            console.error('Errore nella creazione della sala:', err);
            toast({
                title: "Errore",
                description: "Errore nella creazione della sala",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (loading) return (
        <Center minH="60vh"><Spinner size="xl" /></Center>
    );

    return (
        <Box p={6}>
            <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                mb={6}
                gap={4}
            >
                <Heading>Seleziona una Sala</Heading>
                <Button colorScheme="green" onClick={onCreateOpen} w={{ base: '100%', md: 'auto' }}>
                    Crea Sala
                </Button>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {rooms.map(r => (
                    <Card key={r._id} cursor="pointer" onClick={() => navigate('/dashboard', { state: { room: r } })} _hover={{ shadow: 'lg', transform: 'translateY(-4px)' }}>
                        <CardBody>
                            <VStack align="start" spacing={2}>
                                <Heading size="md">{r.titoloEvento || 'Sala'}</Heading>
                                <Text><strong>Luogo:</strong> {r.luogo}</Text>
                                <Text><strong>Data:</strong> {new Date(r.data).toLocaleDateString('it-IT')}</Text>
                            </VStack>
                        </CardBody>
                    </Card>
                ))}
            </SimpleGrid>

            {rooms.length === 0 && (
                <Center py={10}>
                    <Text>Nessuna sala trovata</Text>
                </Center>
            )}

            {/* Modal Crea Sala */}
            <Modal isOpen={isCreateOpen} onClose={onCreateClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Crea nuova sala</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Configurazione</FormLabel>
                            <Select value={selectedConfig} onChange={(e) => setSelectedConfig(e.target.value)}>
                                <option value="trento">Trento</option>
                                <option value="fai">Fai della Paganella</option>
                            </Select>
                        </FormControl>

                        <FormControl mt={3}>
                            <FormLabel>Prezzo</FormLabel>
                            <Input
                                type="number"
                                value={selectedPrice}
                                onChange={(e) => setSelectedPrice(parseFloat(e.target.value))}
                            />
                        </FormControl>

                        <FormControl mt={3}>
                            <FormLabel>Titolo Evento</FormLabel>
                            <Input
                                type="text"
                                value={titoloEvento}
                                onChange={(e) => setTitoloEvento(e.target.value)}
                                placeholder="Inserisci il titolo dell'evento"
                            />
                        </FormControl>

                        <FormControl mt={3}>
                            <FormLabel>Data evento</FormLabel>
                            <Input
                                type="date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                            />
                        </FormControl>

                        <FormControl mt={3}>
                            <FormLabel>Immagine Evento</FormLabel>
                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onload = (event) => {
                                            const img = new Image();
                                            img.onload = () => {
                                                const canvas = document.createElement('canvas');
                                                const MAX_WIDTH = 800;
                                                const MAX_HEIGHT = 600;
                                                let width = img.width;
                                                let height = img.height;

                                                if (width > height) {
                                                    if (width > MAX_WIDTH) {
                                                        height *= MAX_WIDTH / width;
                                                        width = MAX_WIDTH;
                                                    }
                                                } else {
                                                    if (height > MAX_HEIGHT) {
                                                        width *= MAX_HEIGHT / height;
                                                        height = MAX_HEIGHT;
                                                    }
                                                }

                                                canvas.width = width;
                                                canvas.height = height;
                                                const ctx = canvas.getContext('2d');
                                                ctx?.drawImage(img, 0, 0, width, height);

                                                const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                                                setImmagineBase64(compressedBase64);
                                            };
                                            img.src = event.target?.result as string;
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                            />
                            {immagineBase64 && (
                                <Text fontSize="sm" color="green.500" mt={2}>
                                    ✓ Immagine caricata ({(immagineBase64.length / 1024).toFixed(0)} KB)
                                </Text>
                            )}
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={handleCreateRoom}>
                            Crea
                        </Button>
                        <Button variant="ghost" onClick={onCreateClose}>Annulla</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default RoomSelectionPage;